/**
 * AP Aging Report — /finance/reports/ap-aging
 *
 * Outstanding payables grouped by aging bucket (positive = overdue).
 * Cash-flow focus: shows what's due and when.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  RefreshCw, Search, Download, AlertCircle, Receipt, ArrowRight, BrainCircuit, CalendarClock,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/lib/toast';
import { FinanceWorkspaceShell } from '../FinanceWorkspaceShell';
import { buildSoaRoute, downloadAuthenticatedCsv, fmtNumber, hasResolvableSoaPartyRef, todayIso } from './report-utils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ApAgingRow {
  vendor_id: string;
  vendor_name: string;
  currency: string;
  current: number;
  days_1_30: number;
  days_31_60: number;
  days_61_90: number;
  days_over_90: number;
  total: number;
  next_due_date?: string;
}

interface ApAgingSummary {
  as_of_date: string;
  currency: string;
  current_total: number;
  days_1_30_total: number;
  days_31_60_total: number;
  days_61_90_total: number;
  days_over_90_total: number;
  grand_total: number;
  rows: ApAgingRow[];
  totalBillCount?: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ApAgingPage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState<ApAgingSummary | null>(null);
  const [search, setSearch] = useState('');
  const [asOf, setAsOf] = useState(todayIso());
  const [currencyView, setCurrencyView] = useState<'functional' | 'presentation'>('functional');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        asOf,
        currency_view: currencyView,
      });
      if (search.trim()) params.set('search', search.trim());
      const res = await api.get<ApAgingSummary>(`/finance/reports/ap-aging?${params}`);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [asOf, currencyView, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        asOf,
        currency_view: currencyView,
        format: 'csv',
      });
      if (search.trim()) params.set('search', search.trim());
      await downloadAuthenticatedCsv(`/finance/reports/ap-aging?${params}`, `ap-aging-${asOf}.csv`);
      toast.success('AP Aging CSV 已下载');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '导出失败');
    } finally {
      setExporting(false);
    }
  }, [asOf, currencyView, search, toast]);

  const rows = data?.rows ?? [];
  const topRiskRows = useMemo(
    () => [...rows].sort((a, b) => b.days_over_90 - a.days_over_90).filter((row) => row.days_over_90 > 0).slice(0, 5),
    [rows],
  );
  const dueSoonEstimate = useMemo(
    () => (data?.current_total ?? 0) + (data?.days_1_30_total ?? 0),
    [data],
  );
  const statementFrom = `${asOf.slice(0, 4)}-01-01`;

  const buckets = [
    { key: 'current',      label: 'Not Yet Due', color: 'text-green-700' },
    { key: 'days_1_30',    label: '1–30 days',   color: 'text-yellow-700' },
    { key: 'days_31_60',   label: '31–60 days',  color: 'text-orange-600' },
    { key: 'days_61_90',   label: '61–90 days',  color: 'text-red-600' },
    { key: 'days_over_90', label: '90+ days',    color: 'text-red-800 font-bold' },
  ];

  return (
    <FinanceWorkspaceShell
      title="AP Aging"
      subtitle="Open payable exposure grouped by aging bucket"
      Icon={Receipt}
      iconClassName="text-orange-600"
      moduleBadge="Report 5.2"
      domainBadge="Cash-out planning"
      agentTitle="Payables Pressure Report"
      agentSummary="This report turns AP aging into a working-capital control surface: as-of due pressure, overdue concentration, and direct drill-forward into payment execution and reconciliation."
      checklistTitle="AP aging readiness"
      checklistStatus="Due-pressure report"
      checklistNote="The page is now testable with as-of logic, authenticated export, and explicit high-risk overdue vendor visibility."
      checklistItems={[
        'Aging is recalculated for the selected as-of date.',
        'CSV export now works with authenticated download.',
        'Overdue vendor concentration is surfaced for payment planning.',
      ]}
      quickActions={[
        { label: 'AP Workspace', to: '/finance/ap' },
        { label: 'Payment Run', to: '/finance/payment-run' },
        { label: 'Payments', to: '/finance/payments' },
        { label: 'Process Audit', to: '/finance/reports/process-audit', variant: 'secondary' },
      ]}
      headerActions={(
        <>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || loading}>
            <Download className={`h-4 w-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} /> Export CSV
          </Button>
        </>
      )}
    >

      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div>
            <p className="text-xs text-muted-foreground mb-1">As Of</p>
            <Input type="date" className="w-40" value={asOf} onChange={e => setAsOf(e.target.value)} />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Currency View</p>
            <Select value={currencyView} onValueChange={value => setCurrencyView(value as 'functional' | 'presentation')}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="functional">Functional</SelectItem>
                <SelectItem value="presentation">Presentation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search vendor or ID…" className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <Button onClick={fetchData} disabled={loading}>Run Report</Button>
        </CardContent>
      </Card>

      {/* Bucket totals */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {buckets.map(b => (
            <Card key={b.key} className="p-3">
              <div className="text-xs text-muted-foreground">{b.label}</div>
              <div className={`text-sm font-bold mt-1 ${b.color}`}>
                {fmtNumber((data as unknown as Record<string, number>)[`${b.key}_total`] ?? 0)}
              </div>
            </Card>
          ))}
          <Card className="p-3 bg-muted/30">
            <div className="text-xs font-medium text-muted-foreground">Grand Total</div>
            <div className="text-sm font-bold mt-1">{fmtNumber(data.grand_total)}</div>
          </Card>
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BrainCircuit className="h-4 w-4 text-primary" />
              AP AI Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-4">
            <div className="rounded-xl border p-3">
              <p className="text-xs text-muted-foreground">Open Bills</p>
              <p className="mt-1 text-2xl font-semibold">{data?.totalBillCount ?? 0}</p>
              <p className="text-xs text-muted-foreground">as of {data?.as_of_date ?? asOf}</p>
            </div>
            <div className="rounded-xl border p-3">
              <p className="text-xs text-muted-foreground">Due Soon</p>
              <p className="mt-1 text-2xl font-semibold text-yellow-700">{fmtNumber(dueSoonEstimate)}</p>
              <p className="text-xs text-muted-foreground">current + 30d bucket</p>
            </div>
            <div className="rounded-xl border p-3">
              <p className="text-xs text-muted-foreground">90+ Overdue</p>
              <p className="mt-1 text-2xl font-semibold text-red-700">{fmtNumber(data?.days_over_90_total ?? 0)}</p>
              <p className="text-xs text-muted-foreground">highest risk bucket</p>
            </div>
            <div className="rounded-xl border p-3">
              <p className="text-xs text-muted-foreground">Current View</p>
              <p className="mt-1 text-sm font-semibold">{currencyView}</p>
              <p className="text-xs text-muted-foreground">{data?.currency ?? 'Functional'}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-primary" />
              Top Overdue Vendors
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {topRiskRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No 90+ day vendor exposures in the current result set.</p>
            ) : topRiskRows.map((row) => (
              <div key={row.vendor_id} className="rounded-xl border p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{row.vendor_name}</p>
                    <p className="text-xs text-muted-foreground">{row.vendor_id}</p>
                  </div>
                  <Badge variant="outline" className="border-red-200 text-red-700">90+</Badge>
                </div>
                <p className="mt-2 text-sm text-red-700 font-semibold">{fmtNumber(row.days_over_90)}</p>
                <div className="mt-3 flex justify-end">
                  {hasResolvableSoaPartyRef(row.vendor_id) ? (
                    <Button asChild size="sm" variant="outline">
                      <Link to={buildSoaRoute({ view: 'ap', partyId: row.vendor_id, partyName: row.vendor_name, from: statementFrom, to: asOf, autostart: true })}>
                        Open SoA
                      </Link>
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" disabled>
                      SoA unavailable
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="font-medium whitespace-nowrap">Vendor</TableHead>
                <TableHead className="font-medium whitespace-nowrap text-right text-green-700">Not Due</TableHead>
                <TableHead className="font-medium whitespace-nowrap text-right text-yellow-700">1–30</TableHead>
                <TableHead className="font-medium whitespace-nowrap text-right text-orange-600">31–60</TableHead>
                <TableHead className="font-medium whitespace-nowrap text-right text-red-600">61–90</TableHead>
                <TableHead className="font-medium whitespace-nowrap text-right text-red-800">90+</TableHead>
                <TableHead className="font-medium whitespace-nowrap text-right">Total</TableHead>
                <TableHead className="font-medium whitespace-nowrap text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                  <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading report…
                </TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="py-12 text-center">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" />
                  <p className="text-muted-foreground text-sm">No data available</p>
                </TableCell></TableRow>
              ) : rows.map(r => (
                <TableRow key={r.vendor_id} className="hover:bg-muted/20">
                  <TableCell>
                    <div className="font-medium text-sm">{r.vendor_name}</div>
                    <div className="text-xs text-muted-foreground">{r.currency}{r.next_due_date ? ` · due ${r.next_due_date}` : ''}</div>
                  </TableCell>
                  <TableCell className="text-right text-green-700">{fmtNumber(r.current)}</TableCell>
                  <TableCell className="text-right text-yellow-700">{r.days_1_30 > 0 ? fmtNumber(r.days_1_30) : '—'}</TableCell>
                  <TableCell className="text-right text-orange-600">{r.days_31_60 > 0 ? fmtNumber(r.days_31_60) : '—'}</TableCell>
                  <TableCell className="text-right text-red-600">{r.days_61_90 > 0 ? fmtNumber(r.days_61_90) : '—'}</TableCell>
                  <TableCell className="text-right text-red-800 font-medium">{r.days_over_90 > 0 ? fmtNumber(r.days_over_90) : '—'}</TableCell>
                  <TableCell className="text-right font-bold">{fmtNumber(r.total)}</TableCell>
                  <TableCell className="text-right">
                    {hasResolvableSoaPartyRef(r.vendor_id) ? (
                      <Button asChild size="sm" variant="outline">
                        <Link to={buildSoaRoute({ view: 'ap', partyId: r.vendor_id, partyName: r.vendor_name, from: statementFrom, to: asOf, autostart: true })}>
                          SoA
                        </Link>
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" disabled>
                        SoA unavailable
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
      <div className="flex justify-end">
        <Button asChild variant="outline" size="sm">
          <Link to="/finance/ap">
            Open AP workspace
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </FinanceWorkspaceShell>
  );
}
